/**
 * What a bulk SMS send means for the operator — and what the modal must do with what is left.
 *
 * Pure, and out of the component, because the two mistakes it prevents are not visual: a send that MAY
 * have gone out must not be reported as a refusal (the operator presses send again and the driver reads
 * the same text twice, billed per segment), and whatever did go out must not stay selected behind an
 * enabled button.
 */
import { SmsBulkSendResult } from '@numeo/types'

interface BulkSendVerdict {
    /** One line for the toast, in the order an operator reads it: what happened, then what did not. */
    summary: string
    /** True when anything was handed to Quo — including the unconfirmed, which may be on a handset. */
    handedOver: boolean
    /**
     * Drivers worth selecting again: the ones Quo REFUSED, and the ones it was never asked about because
     * the send stopped at a billing refusal. Never the unconfirmed ones — those may be on a handset.
     */
    retryable: string[]
    /** Nothing left to retry, so the modal has no reason to stay open. */
    done: boolean
}

export const readBulkSend = (result: SmsBulkSendResult, selectedIds: string[]): BulkSendVerdict => {
    const unconfirmed = result.unconfirmed ?? []
    // WHO WAS NEVER ASKED ABOUT. The send stops at the first billing refusal rather than spending the
    // rest of its requests on a workspace with no credit — right, and invisible until this read it: the
    // response carried the ids, nothing here looked at them, and `setSelectedIds(retryable)` then kept
    // only the refused ones. A 100-driver send that met a 402 in its first batch deselected 92 drivers
    // who had never been texted, told the operator "0 sent · 8 refused", and looked finished.
    const skipped = (result.skippedAfterBillingRefusal ?? []).filter((id) => selectedIds.includes(id))
    const parts = [`${result.sent} sent`]
    if (result.skippedNoPhone) parts.push(`${result.skippedNoPhone} without a phone`)
    if (result.failed.length) parts.push(`${result.failed.length} refused`)
    // Its own wording: "refused" is an invitation to try again, and these are the one case where that
    // is the wrong advice.
    if (unconfirmed.length) parts.push(`${unconfirmed.length} unconfirmed — may have been sent`)
    // Named as NOT TRIED, which is the honest word and the one that tells the operator the button is
    // still worth pressing once the workspace can pay.
    if (skipped.length) parts.push(`${skipped.length} not tried — Quo refused this workspace on billing grounds`)
    const refused = result.failed.map((f) => f.driverAccountId).filter((id) => selectedIds.includes(id))
    // Both stay selected: one set Quo turned down and one it was never asked about, and pressing send
    // again is the right move for either.
    const retryable = [...new Set([...refused, ...skipped])]
    return {
        summary: parts.join(' · '),
        handedOver: result.sent > 0 || unconfirmed.length > 0,
        retryable,
        done: retryable.length === 0,
    }
}

/** The analytics bucket for the same send. `unconfirmed` only when NOTHING is known to have landed. */
export const bulkSendOutcome = (result: SmsBulkSendResult): 'sent' | 'partial' | 'none' | 'unconfirmed' => {
    const unconfirmed = result.unconfirmed ?? []
    if (!result.sent) return unconfirmed.length ? 'unconfirmed' : 'none'
    return result.failed.length || unconfirmed.length ? 'partial' : 'sent'
}
